import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogContentText, IconButton, Table, TableCell, TableRow, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from "react";
import SquareIcon from '@mui/icons-material/Square';
import CheckIcon from '@mui/icons-material/Check';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { isEmpty } from '../../../utils/Util';
import { TargetPayApi } from '../../../utils/UtilTarget';

export interface OnBoardingStatusDetailsPopupPorps {
	openPopup: boolean;
	selectedDealer: any;
	onClose: (value: boolean) => void;
	onValidated?: () => void;
}

export default function OnBoardingStatusDetails(props: OnBoardingStatusDetailsPopupPorps) {
	const { openPopup, selectedDealer, onClose, onValidated } = props;
	const [isValidating, setIsValidating] = React.useState(false);
	const [validationSuccess, setValidationSuccess] = React.useState(false);
	const [verificationStatus, setVerificationStatus] = React.useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = React.useState(false);

	React.useEffect(() => {
		setVerificationStatus(selectedDealer?.verificationStatus ?? null);
		setValidationSuccess(false);
	}, [selectedDealer]);

	const handleClose = () => {
		onClose(false);
	};

	const handleSetToValid = async () => {
		setConfirmOpen(false);
		setIsValidating(true);
		const result = await TargetPayApi("ValidateDealerOnboarding", selectedDealer.dealerId, "post", "Support/Dealer");
		setIsValidating(false);
		if (result === true) {
			setVerificationStatus("valid");
			setValidationSuccess(true);
			if (onValidated) onValidated();
		}
	};

	const parsedProblems = React.useMemo(() => {
		if (isEmpty(selectedDealer?.verificationProblems)) return [];
		try {
			return JSON.parse(selectedDealer.verificationProblems);
		} catch {
			return [];
		}
	}, [selectedDealer?.verificationProblems]);

	const isNotValid = verificationStatus?.toLowerCase() !== 'valid' && !isEmpty(selectedDealer?.sweepDateCreated);

	return (
		<>
		<Dialog maxWidth="md" fullWidth={true} open={openPopup}>
			<DialogTitle> {selectedDealer?.name} {" - "} {selectedDealer?.dmsDealerId}</DialogTitle>
			<div style={{ position: 'absolute', right: 10, top: 10 }}>
				<IconButton aria-label="close" onClick={handleClose}
					sx={{ color: (theme) => theme.palette.grey[500] }}>
					<CloseIcon />
				</IconButton>
			</div>
			<DialogContent dividers className="timeline-help">
				<Table size="small">
					<TableRow>
						<TableCell sx={{ fontWeight: "bold", width: "350px" }}>
							Status</TableCell>
						<TableCell valign='middle'>
							<Box display="flex" alignItems="center">
								{selectedDealer?.status?.indexOf("progress") > -1
									? <FlipCameraAndroidIcon sx={{ fontSize: "20px", color: "orange" }} />
									: <CheckIcon sx={{ fontSize: "20px", color: "green" }} />}
								<span style={{ color: selectedDealer?.status?.indexOf("progress") > -1 ? "orange" : "green", marginLeft: "8px" }}>
									{selectedDealer?.status}
								</span>
							</Box>
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: "bold", width: "350px" }}>
							<SquareIcon sx={{ fontSize: "6px", marginRight: "8px" }} />Verification Status</TableCell>
						<TableCell>{verificationStatus ?? "N/A"}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: "bold", width: "350px" }}>
							<SquareIcon sx={{ fontSize: "6px", marginRight: "8px" }} />Transfer Instrument Verified Date</TableCell>
						<TableCell>{selectedDealer?.transferInstrumentDateEnabled ? selectedDealer.transferInstrumentDateEnabled.toUSDateFormat() : "N/A"}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: "bold", width: "350px" }}>
							<SquareIcon sx={{ fontSize: "6px", marginRight: "8px" }} />Sweep Created Date</TableCell>
						<TableCell>{selectedDealer?.sweepDateCreated ? selectedDealer.sweepDateCreated.toUSDateFormat() : "N/A"}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell sx={{ fontWeight: "bold", width: "350px" }}>
							<SquareIcon sx={{ fontSize: "6px", marginRight: "8px" }} />Onboarding Completion Date</TableCell>
						<TableCell>{selectedDealer?.onboardingCompletionDate ? selectedDealer.onboardingCompletionDate.toUSDateFormat() : "N/A"}</TableCell>
					</TableRow>
				</Table>

				{parsedProblems.length > 0 && (
					<Box mt={2}>
						<Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "error.main", mb: 1 }}>
							Verification Problems &amp; Errors
						</Typography>
						{parsedProblems.map((problem: any, pIdx: number) => (
							<Box key={pIdx} mb={1}>
								{problem.verificationErrors?.map((err: any, eIdx: number) => (
									<Typography key={eIdx} variant="body2" color="error" sx={{ fontSize: "small" }}>
										{err.code}: {err.message}
									</Typography>
								))}
							</Box>
						))}
					</Box>
				)}

				{validationSuccess && (
					<Alert severity="success" sx={{ mt: 2 }}>
						Dealer verification status has been set to valid.
					</Alert>
				)}
			</DialogContent>

			{isNotValid && (
				<DialogActions>
					<Button
						variant="contained"
						color="primary"
						disabled={isValidating || validationSuccess}
						onClick={() => setConfirmOpen(true)}
						startIcon={isValidating ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						{isValidating ? "Processing..." : "Set to Valid"}
					</Button>
				</DialogActions>
			)}
		</Dialog>
				<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
			<DialogTitle>Confirm KYC Validation</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Please confirm that this dealer has passed all KYC checks on Adyen.
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
				<Button variant="contained" color="primary" onClick={handleSetToValid}>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
		</>
	);
}
